import { trans, plainTrans } from "@mongez/localization";
import { Search, SlidersHorizontal, Sparkles, X } from "lucide-react";

type FilterOption = {
  value: string;
  label: string;
};

type ActiveFilter = {
  key: "search" | "grade" | "category";
  label: string;
};

type LessonsCatalogFiltersProps = {
  activeFilters: ActiveFilter[];
  categoryOptions: FilterOption[];
  gradeOptions: FilterOption[];
  hideGradeFilter: boolean;
  searchTerm: string;
  selectedCategory: string;
  selectedGrade: string;
  onCategoryChange: (value: string) => void;
  onClearFilters: () => void;
  onClearSearch: () => void;
  onGradeChange: (value: string) => void;
  onRemoveFilter: (key: ActiveFilter["key"]) => void;
  onSearchChange: (value: string) => void;
  onSearchSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
};

export function LessonsCatalogFilters({
  activeFilters,
  categoryOptions,
  gradeOptions,
  hideGradeFilter,
  searchTerm,
  selectedCategory,
  selectedGrade,
  onCategoryChange,
  onClearFilters,
  onClearSearch,
  onGradeChange,
  onRemoveFilter,
  onSearchChange,
  onSearchSubmit,
}: LessonsCatalogFiltersProps) {
  return (
    <section className="relative z-20 -mt-3 rounded-[1.75rem] border border-slate-200/80 bg-white/95 p-4 shadow-xl shadow-slate-900/5 backdrop-blur-xl dark:border-slate-700 dark:bg-slate-900/95 sm:p-5">
      <div className="mb-4 flex items-center gap-2">
        <span className="grid size-9 place-items-center rounded-xl bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-200">
          <SlidersHorizontal className="size-4" />
        </span>
        <div>
          <h2 className="text-sm font-black text-slate-950 dark:text-white sm:text-base">
            {trans("lessonCatalog.findYourLesson")}
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {trans("lessonCatalog.filtersHint")}
          </p>
        </div>
      </div>

      <div
        className={`grid gap-3 ${
          hideGradeFilter
            ? "lg:grid-cols-[minmax(0,1.8fr)_minmax(220px,1fr)]"
            : "lg:grid-cols-[minmax(0,1.6fr)_repeat(2,minmax(190px,0.8fr))]"
        }`}
      >
        <form className="relative" onSubmit={onSearchSubmit}>
          <Search className="pointer-events-none absolute start-4 top-1/2 size-5 -translate-y-1/2 text-slate-400" />
          <input
            aria-label={plainTrans("grades.searchLessons")}
            className="dark:focus:ring-primary-950 h-14 w-full rounded-2xl border border-slate-200 bg-slate-50 pe-36 ps-12 text-sm font-medium text-slate-900 outline-none transition focus:border-primary-400 focus:bg-white focus:ring-4 focus:ring-primary-100 dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:focus:border-primary-500 dark:focus:bg-slate-900"
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={plainTrans("lessonCatalog.searchPlaceholder")}
            type="search"
            value={searchTerm}
          />
          {searchTerm && (
            <button
              aria-label={plainTrans("lessonCatalog.clearSearch")}
              className="absolute end-24 top-1/2 grid size-8 -translate-y-1/2 place-items-center rounded-full text-slate-400 transition hover:bg-slate-200 hover:text-slate-700 dark:hover:bg-slate-700 dark:hover:text-white"
              onClick={onClearSearch}
              type="button"
            >
              <X className="size-4" />
            </button>
          )}
          <button
            className="absolute end-2 top-1/2 grid h-10 -translate-y-1/2 place-items-center rounded-xl bg-slate-950 px-4 text-xs font-bold text-white transition hover:bg-primary-700 dark:bg-primary-600 dark:hover:bg-primary-500"
            type="submit"
          >
            {trans("lessonCatalog.searchButton")}
          </button>
        </form>

        {!hideGradeFilter && (
          <label className="relative">
            <span className="sr-only">{trans("grades.allGrades")}</span>
            <select
              className="dark:focus:ring-primary-950 h-14 w-full appearance-none rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-800 outline-none transition focus:border-primary-400 focus:ring-4 focus:ring-primary-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
              onChange={(event) => onGradeChange(event.target.value)}
              value={selectedGrade}
            >
              <option value="">{trans("grades.allGrades")}</option>
              {gradeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="relative">
          <span className="sr-only">{trans("grades.allCategories")}</span>
          <select
            className="dark:focus:ring-primary-950 h-14 w-full appearance-none rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-800 outline-none transition focus:border-primary-400 focus:ring-4 focus:ring-primary-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
            onChange={(event) => onCategoryChange(event.target.value)}
            value={selectedCategory}
          >
            <option value="">{trans("grades.allCategories")}</option>
            {categoryOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {activeFilters.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4 dark:border-slate-800">
          <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 dark:text-slate-400">
            <Sparkles className="size-3.5 text-amber-500" />
            {trans("lessonCatalog.activeFilters")}
          </span>
          {activeFilters.map((filter) => (
            <button
              className="dark:bg-primary-950 inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1.5 text-xs font-bold text-primary-700 transition hover:bg-primary-100 dark:text-primary-200 dark:hover:bg-primary-900"
              key={filter.key}
              onClick={() => onRemoveFilter(filter.key)}
              type="button"
            >
              {filter.label}
              <X className="size-3.5" />
            </button>
          ))}
          <button
            className="text-xs font-bold text-rose-600 underline-offset-4 hover:underline dark:text-rose-400"
            onClick={onClearFilters}
            type="button"
          >
            {trans("lessonCatalog.clearAll")}
          </button>
        </div>
      )}
    </section>
  );
}
